// Playable circuits: a Track plus the session layout (surfaces, barriers,
// active-aero zones, sectors) derived from the generated track geometry.

import { Track } from './track';
import { SEPANG_CENTRELINE } from './data/sepang';
import { SEPANG_ELEVATION } from './data/sepangElevation';
import { barrierOffsetAt, getLayout, surfaceAt } from './geometry';
import type { TrackLayout } from '../sim/session';
import { sepangPitLane } from './pitLane';

/** Straights at least this long get an active-aero activation zone. */
const MIN_ZONE_STRAIGHT = 500;
/** Zone starts this far after a corner ends, and ends this far before the next one starts. */
const ZONE_START_AFTER = 80;
const ZONE_END_BEFORE = 150;

/**
 * Activation zones on the long straights. Sepang isn't on the 2026 calendar,
 * so there are no FIA-published zones; this follows the FIA's practice of
 * zones only where cars aren't at the grip limit, each lasting several seconds.
 */
export function aeroZonesFor(track: Track): Array<[number, number]> {
  const geo = getLayout(track);
  const L = geo.length;
  const at = (i: number): number => geo.cum[i % geo.count]! + (i >= geo.count ? L : 0);
  const corners = [...geo.corners].sort((a, b) => a.s - b.s);
  const zones: Array<[number, number]> = [];
  corners.forEach((c, k) => {
    const next = corners[(k + 1) % corners.length]!;
    const start = at(c.end);
    let end = at(next.start);
    if (end <= start) end += L;
    if (end - start < MIN_ZONE_STRAIGHT) return;
    zones.push([(start + ZONE_START_AFTER) % L, (end - ZONE_END_BEFORE) % L]);
  });
  return zones;
}

/** Approximate apex distance of Sepang's corners on our centreline, metres. */
const SEPANG_APEX = { T1: 705, T3: 1075, T4: 1599, T8: 2689, T9: 3179, T14: 4238, T15: 5193 } as const;

/**
 * The four Straight Mode zones the FIA used at Sepang in 2026 (event notes,
 * docs/research/sepang-2026.md): each starts a set distance after a corner's
 * exit and runs to the next braking zone. Anchored to our detected corners, as
 * our lap distances differ slightly from the FIA's.
 */
export function sepangAeroZones(track: Track): Array<[number, number]> {
  const geo = getLayout(track);
  const L = geo.length;
  const corner = (apexS: number) =>
    geo.corners.reduce((best, c) => (Math.abs(c.s - apexS) < Math.abs(best.s - apexS) ? c : best));
  const exitOf = (apexS: number): number => geo.cum[corner(apexS).end % geo.count]!;
  const turnInOf = (apexS: number): number => geo.cum[corner(apexS).start % geo.count]!;
  // Stop short of the corner; braking closes the wings earlier anyway.
  const BEFORE_TURN_IN = 50;
  const zone = (fromApex: number, after: number, toApex: number): [number, number] => [
    (exitOf(fromApex) + after) % L,
    (turnInOf(toApex) - BEFORE_TURN_IN + L) % L,
  ];
  return [
    zone(SEPANG_APEX.T15, 65, SEPANG_APEX.T1), // A1: main straight
    zone(SEPANG_APEX.T3, 10, SEPANG_APEX.T4), // A2: T3 exit to T4
    zone(SEPANG_APEX.T8, 65, SEPANG_APEX.T9), // A3: T8 exit to T9
    zone(SEPANG_APEX.T14, 65, SEPANG_APEX.T15), // A4: back straight
  ];
}

/** FIA sector lines at Sepang: 1,435 m and 3,274 m of the official 5,543 m lap. */
const SEPANG_OFFICIAL_LENGTH = 5543;
const SEPANG_SECTOR_LINES = [1435, 3274];
const SEPANG_ALT1_SECTORS: Array<[number, number]> = [
  [1550, 2500],
  [3100, 4100],
];

export function sepang(): TrackLayout {
  // Elevation from Copernicus GLO-30 (docs/research/sepang-geometry.md): ~21 m range, T3 low, T11 high.
  const track = new Track(SEPANG_CENTRELINE, SEPANG_ELEVATION);
  const pitLane = sepangPitLane(track);
  return {
    track,
    pitLane,
    surfaceAt: (p) => surfaceAt(track, p),
    barrierOffset: (p) => {
      const b = barrierOffsetAt(track, p);
      // The pit wall closes in on the pit side along the main straight.
      const wall = pitLane.wallOffsetAt(p.s);
      const pitSide = wall === undefined ? b : Math.min(b, wall);
      return pitLane.entryOffsetSide === 1 ? { right: pitSide, left: b } : { right: b, left: pitSide };
    },
    aeroZones: sepangAeroZones(track),
    sectorEnds: SEPANG_SECTOR_LINES.map((s) => (s * track.length) / SEPANG_OFFICIAL_LENGTH),
    // Out of the final hairpin onto the main straight (T15 is ~305 m before the line).
    flyingStartDistance: 260,
    // Race "Alt 1" ERS curve at Sepang 2026: T4–T7 and T9–T14 (FIA PU information, Doc 4).
    raceDeploySectors: SEPANG_ALT1_SECTORS.map(([a, b]): [number, number] => [(a * track.length) / SEPANG_OFFICIAL_LENGTH, (b * track.length) / SEPANG_OFFICIAL_LENGTH]),
  };
}
